import OptionType from 'features/shared/components/selectInput/OptionType';
import React, { useEffect, useState } from 'react';
import { AccountRestrictionType } from 'features/accounts/enums/accountRestrictionType';
import { useTranslation } from 'react-i18next';
import FormGroup from 'features/shared/forms/components/FormGroup';
import Select from "react-select";
import reactSelectStyles from "features/shared/components/reactSelect/reactselectcustom.module.scss";
import { Nullable } from 'features/shared/types/nullable';
import AccountRestrictionDTO from '../dtos/AccountRestrictionDTO';
import ContractDropdownDTO from 'features/contracts/dtos/ContractDropdownDTO';
import ContractualAccountDTO from 'features/contracts/dtos/ContractualAccountDTO';
import ContractSelect from 'features/contracts/components/ContractSelect';
import ContractualAccountSelect from 'features/contracts/components/ContractualAccountSelect';
import { containsKey } from 'features/shared/helpers/objects';

interface Props {
  contracts: ContractDropdownDTO[];
  contractualAccounts: ContractualAccountDTO[];
  selectedRestriction: Nullable<AccountRestrictionDTO>;
  setSelectedRestrictiton: React.Dispatch<React.SetStateAction<Nullable<AccountRestrictionDTO>>>;
  fullInputsWidth?: boolean;
}

const AccountRestrictionSelect = ({
  contracts,
  contractualAccounts,
  selectedRestriction,
  setSelectedRestrictiton,
  fullInputsWidth = false,
}: Props) => {
  // tranlations
  const { t } = useTranslation("accounts\\enums\\accountRestrictionType");
  const tLabels = useTranslation("accounts\\accounts").t;

  // helpers
  const getRestrictionTypes = (): OptionType[] => {
    return Object.keys(AccountRestrictionType)
      // docasne vypnuti zakladani omezeni na OPM (kvuli ODS)
      .filter((x) => x !== AccountRestrictionType.Undefined && x !== AccountRestrictionType.PlaceOfDelivery)
      .map((type) => {
        return { name: t(type), value: type };
      });
  }

  const getSelectedRestricionType = (): Nullable<OptionType> => {
    if (selectedRestriction?.entity) {
      return {
        name: t(selectedRestriction.entity),
        value: selectedRestriction.entity
      }
    } else {
      return null;
    }
  }

  const getSelectedRestriction = <T, >(data: T[], filterProperty: string): Nullable<T> => {
    if (selectedRestriction && containsKey(selectedRestriction, filterProperty)) {
      let item = data.find(x => (x as any)[filterProperty] === (selectedRestriction as any)[filterProperty]);
      
      return item ?? null;
    } 
    
    return null;
  }

  // handlers
  const restrictionTypeChangeHandler = (restrictionType: Nullable<OptionType>) => {
    if (restrictionType) {
      setSelectedRestrictiton({ entity:  restrictionType.value as AccountRestrictionType});
    }
  } 

  const contractulAccountChangeHandler = (account: Nullable<ContractualAccountDTO>) => {
    if (account) {
      let restriction: AccountRestrictionDTO = {
        entity: selectedRestriction?.entity,
        contractualAccountId: account.contractualAccountId,
        contractualAccountNumber: account.variableSymbol
      };

      setSelectedRestrictiton(restriction);
    }
  } 

  const contractChangeHandler = (contract: Nullable<ContractDropdownDTO>) => {
    if (contract) {     
      let restriction: AccountRestrictionDTO = {
        entity: selectedRestriction?.entity,
        placeOfDeliveryId: contract.placeOfDeliveryId,
        placeOfDeliveryIdentification: contract.placeOfDelivery
      };

      setSelectedRestrictiton(restriction);
    }
  } 

  // sub-renders
  const renderSelectByRestrictionType = () => {
    switch (selectedRestriction?.entity) {
      case "ContractualAccount":
        return (
          <ContractualAccountSelect 
            name="AccountRestrictionContractualAccount"
            contractualAccounts={contractualAccounts}
            onChange={contractulAccountChangeHandler}
            selectedContractualAccount={getSelectedRestriction(contractualAccounts, "contractualAccountId")}
          />
        );
      case "PlaceOfDelivery":
        return (
          <ContractSelect 
            name="AccountRestrictionContract"
            contracts={contracts}
            onChange={contractChangeHandler}
            defaultContract={getSelectedRestriction(contracts, "placeOfDeliveryId")}
            type="PlaceOfDelivery"
          />
        );
      default:
        return (
          <FormGroup labelText={tLabels("accountRestrictionSelect.restrictions")}>
            <input className="form-control" disabled placeholder={tLabels("accountRestrictionSelect.firstSelectRestrictionType")} />
          </FormGroup>
        );
    }    
  }

  // render
  return (
    <div className="row no-gutters mx-n1">
      <div className={fullInputsWidth ? "col-12 px-1" : "col-lg-5 px-1"}>
        <FormGroup labelText={tLabels("accountRestrictionSelect.restrictionType")}>
          <Select
            options={getRestrictionTypes()}
            getOptionLabel={(x) => x.name}
            getOptionValue={(x) => x.value}
            value={getSelectedRestricionType()}
            onChange={restrictionTypeChangeHandler}
            isSearchable={false}
            captureMenuScroll={false}
            className={reactSelectStyles.customSelect}
            classNamePrefix="customSelect"
            placeholder={tLabels("accountRestrictionSelect.selectRestrictionType")}
          />
        </FormGroup>
      </div>
      <div className={fullInputsWidth ? "col-12 px-1" : "col-lg-7 px-1"}>
        {renderSelectByRestrictionType()}
      </div>
    </div>
  )
}

export default AccountRestrictionSelect;
